<template>
  <div class="stat-chart-container">
    <h2>{{ selectedYear }} 業績統計</h2>
    <div class="year-selector-row">
      <label for="year-selector">年份：</label>
      <select id="year-selector" v-model="selectedYear" @change="handleYearChange">
        <option v-for="year in yearOptions" :key="year" :value="year">{{ year }}</option>
      </select>
    </div>
    <div class="tab-navigation">
      <button :class="{ active: activeTab === 'sales' }" @click="activeTab = 'sales'">銷售統計</button>
      <button :class="{ active: activeTab === 'customer' }" @click="activeTab = 'customer'">客戶來源</button>
      <button :class="{ active: activeTab === 'weekend_sales' }" @click="activeTab = 'weekend_sales'">週末銷售</button>
      <button :class="{ active: activeTab === 'bed_sales' }" @click="activeTab = 'bed_sales'">床銷售排行</button>
    </div>
    <div v-if="loading" class="loading">載入中...</div>
    <div v-else>
      <div v-if="activeTab === 'bed_sales'">
        <div class="month-selector-row">
          <label for="bed-month-selector">月份：</label>
          <select id="bed-month-selector" v-model="selectedBedMonth" @change="fetchBedSalesStats">
            <option :value="0">全年度</option>
            <option v-for="month in 12" :key="month" :value="month">{{ month }}月</option>
          </select>
        </div>
        <div class="chart-container">
          <div class="chart-inner">
            <Bar v-if="bedBarChartData" :data="bedBarChartData" :options="bedBarChartOptions" />
          </div>
        </div>
      </div>
      <div v-if="activeTab === 'sales'">
        <div class="chart-container">
          <div class="chart-inner">
            <Bar v-if="chartData" :data="chartData" :options="chartOptions" />
          </div>
        </div>
        <div class="sales-selector-row">
          <label for="sales-selector">業務員：</label>
          <select id="sales-selector" v-model="selectedSalesId" @change="handleSalesChange">
            <option value="">全部</option>
            <option v-for="sales in salesList" :key="sales.id" :value="sales.id">{{ sales.name }}</option>
          </select>
        </div>
        <div class="stat-table-wrapper" :class="{ 'loading-state': tableLoading }">
          <table class="stat-table">
            <thead>
              <tr>
                <th>統計項目</th>
                <th>全年合計</th>
                <th v-for="(month, idx) in this.months" :key="idx">{{ month }}</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>訂單數</td>
                <td>{{ statTable.order_count.reduce((a, b) => a + b, 0) }}</td>
                <td v-for="(val, idx) in statTable.order_count" :key="'order_count-' + idx">{{ val }}</td>
              </tr>
              <template v-for="typeName in statTable.product_type_names" :key="'product_type_count-' + typeName">
                <tr>
                  <td>{{ typeName }}</td>
                  <td>{{ statTable.product_type_count[typeName]?.reduce((a, b) => a + b, 0) }}</td>
                  <td v-for="(val, idx) in statTable.product_type_count[typeName]" :key="'product_type_count-' + typeName + '-' + idx">{{ val }}</td>
                </tr>
              </template>
              <tr>
                <td>折扣</td>
                <td>{{ formatCurrency(statTable.discount_sum.reduce((a, b) => a + b, 0)) }}</td>
                <td v-for="(val, idx) in statTable.discount_sum" :key="'discount_sum-' + idx">{{ formatCurrency(val) }}</td>
              </tr>
              <tr>
                <td>回收</td>
                <td>{{ formatCurrency(statTable.disposal_sum.reduce((a, b) => a + b, 0)) }}</td>
                <td v-for="(val, idx) in statTable.disposal_sum" :key="'disposal_sum-' + idx">{{ formatCurrency(val) }}</td>
              </tr>
              <tr>
                <td>營業額</td>
                <td>{{ formatCurrency(statTable.sales_amount.reduce((a, b) => a + b, 0)) }}</td>
                <td v-for="(val, idx) in statTable.sales_amount" :key="'sales_amount-' + idx">{{ formatCurrency(val) }}</td>
              </tr>
              <tr>
                <td>抽成</td>
                <td>{{ formatCurrency(statTable.share_profit.reduce((a, b) => a + b, 0)) }}</td>
                <td v-for="(val, idx) in statTable.share_profit" :key="'share_profit-' + idx">{{ formatCurrency(val) }}</td>
              </tr>
              <tr>
                <td>來客數</td>
                <td>{{ statTable.customer_traffic.reduce((a, b) => a + b, 0) }}</td>
                <td v-for="(val, idx) in statTable.customer_traffic" :key="'customer_traffic-' + idx">{{ val }}</td>
              </tr>
              <tr>
                <td>成交數</td>
                <td>{{ statTable.deal_count.reduce((a, b) => a + b, 0) }}</td>
                <td v-for="(val, idx) in statTable.deal_count" :key="'deal_count-' + idx">{{ val }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <div v-if="activeTab === 'customer'">
        <div class="chart-container">
          <div class="chart-inner">
            <Pie v-if="customerSourceChartData" :data="customerSourceChartData" :options="customerSourceChartOptions" />
          </div>
        </div>
      </div>
      <div v-if="activeTab === 'weekend_sales'">
        <div class="month-selector-row">
          <label for="month-selector">月份：</label>
          <select id="month-selector" v-model="selectedMonth" @change="fetchWeekendSalesStats">
            <option v-if="selectedYear === currentYear" :value="0">最近一個月</option>
            <option v-for="month in 12" :key="month" :value="month">{{ month }}月</option>
          </select>
        </div>
        <div class="chart-container">
          <div class="chart-inner">
            <Bar v-if="weekendSalesChartData" :data="weekendSalesChartData" :options="weekendSalesChartOptions" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Bar, Pie } from 'vue-chartjs';
import {
  Chart,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  LineController, // Add LineController
  ArcElement, // Add ArcElement for Pie chart
  Legend,
  Tooltip,
  Title
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';

Chart.register(CategoryScale, LinearScale, BarElement, PointElement, LineElement, LineController, ArcElement, Legend, Tooltip, Title, ChartDataLabels);

export default {
  name: 'AnnualStatChart',
  components: { Bar, Pie },
  data() {
    const currentYear = new Date().getFullYear();
    return {
      loading: true, // for chart only
      tableLoading: false, // for table only
      loadingTimeout: null, // for delayed loading state
      activeTab: 'sales', // 'sales', 'customer', or 'weekend_sales'
      chartData: null,
      cachedTrafficData: {}, // Add for caching traffic data
      chartOptions: {
        responsive: true,
        plugins: {
          legend: { position: 'top' },
          title: { display: true, text: '營業額/來客數' },
          datalabels: {
            display: true,
            anchor: 'end',
            align: 'end',
            color: function(context) {
              if (context.dataset.label === '營業額') {
                return 'rgba(54, 162, 235, 1)'; // blue
              } else if (context.dataset.label === '來客數') {
                return 'rgba(255, 206, 86, 1)'; // yellow
              } else if (context.dataset.label === '商品毛利') {
                return 'rgba(255, 99, 132, 1)'; // red
              } else if (context.dataset.label === '成交數') {
                return 'rgba(75, 192, 192, 1)'; // green
              }
              return '#222';
            },
            font: {
              weight: 'bold',
              size: 14
            },
            formatter: function(value) {
              return value ? value.toLocaleString() : '';
            }
          }
        },
        scales: {
          y: {
            beginAtZero: true,
            title: { display: true, text: '營業額' },
            position: 'left',
          },
          y1: {
            beginAtZero: true,
            title: { display: true, text: '來客數' },
            position: 'right',
            grid: { drawOnChartArea: false },
          }
        }
      },
      customerSourceChartData: null,
      customerSourceChartOptions: {
        responsive: true,
        plugins: {
          legend: { position: 'top' },
          title: { display: true, text: '客戶來源分佈' },
          datalabels: {
            color: '#fff',
            formatter: (value, context) => {
              const total = context.dataset.data.reduce((a, b) => a + b, 0);
              const percentage = total > 0 ? ((value / total) * 100).toFixed(1) + '%' : '0%';
              return `${context.chart.data.labels[context.dataIndex]}\n${percentage}`;
            },
            font: {
              weight: 'bold',
              size: 14
            }
          }
        }
      },
      weekendSalesChartData: null, // New data property for weekend sales chart
      weekendSalesChartOptions: { // New options for weekend sales chart
        responsive: true,
        plugins: {
          legend: { position: 'top' },
          title: { display: true, text: '週末銷售統計' },
          datalabels: {
            display: (context) => {
              // Only display label if value > 0 to avoid clutter
              return context.dataset.data[context.dataIndex] > 0;
            },
            anchor: 'end',
            align: 'end',
            color: (context) => {
              if (context.dataset.type === 'line') {
                return context.dataset.borderColor;
              }
              return context.dataset.backgroundColor;
            },
            font: {
              weight: 'bold',
              size: 14
            },
            formatter: function(value) {
              return value ? value.toLocaleString() : '';
            },
            // Ensure datalabels for lines are drawn on top
            order: function(context) {
              if (context.dataset.type === 'line') {
                return 1; // Draw line labels on top
              }
              return 0; // Default order for bars
            }
          }
        },
        scales: {
          y: {
            beginAtZero: true,
            title: { display: true, text: '銷售額' },
            position: 'left',
          },
          y1: {
            beginAtZero: true,
            title: { display: true, text: '數量' },
            position: 'right',
            grid: { drawOnChartArea: false },
          }
        }
      },
      salesList: [],
      salesColors: {}, // To store assigned colors for sales persons
      colorPalette: [ // A palette of distinct colors
        '#4BC0C0', '#9966FF', '#FF9F40', '#FF6384', '#36A2EB', '#FFCE56',
        '#38b539', '#8D6E63', '#FFD700', '#ADFF2F', '#FF69B4', '#BA55D3',
        '#C0C0C0', '#800000', '#808000', '#008000', '#008080', '#000080',
        '#800080', '#A52A2A', '#DAA520', '#7FFF00', '#D2691E', '#6495ED'
      ],
      nextColorIndex: 0,
      selectedSalesId: '',
      currentYear: currentYear,
      selectedYear: currentYear,
      selectedMonth: 0, // Default to "最近一個月"
      selectedBedMonth: 0, // Default to "全年度"
      yearOptions: Array.from({ length: currentYear - 2021 + 1 }, (_, i) => 2021 + i),
      bedSalesData: null,
      bedBarChartData: null,
      bedBarChartOptions: {
        indexAxis: 'y', // Make the bar chart horizontal
        responsive: true,
        maintainAspectRatio: false,
        layout: {
          padding: {
            right: 80 // Increase right padding for data labels
          }
        },
        plugins: {
          legend: { display: false },
          title: { 
            display: true, 
            text: '床產品銷售 Top 20 (數量)',
            font: { size: 18 }
          },
          datalabels: {
            anchor: 'end',
            align: 'right', // Align datalabels to the right for horizontal bars
            formatter: (value, context) => {
              const total = context.chart.data.datasets[0].total || 0;
              const percentage = total > 0 ? ((value / total) * 100).toFixed(1) + '%' : '0%';
              return `${value.toLocaleString()} (${percentage})`;
            },
            font: { 
              weight: 'bold',
              size: 14 // Increase font size for data labels
            }
          }
        },
        scales: {
          x: {
            beginAtZero: true,
            ticks: {
              font: { size: 14 } // Increase x-axis font size
            }
          },
          y: {
            ticks: {
              font: { size: 14 } // Increase y-axis font size
            }
          }
        }
      },
      statTable: {
        order_count: Array(12).fill(0),
        product_type_count: {},
        product_type_names: [],
        discount_sum: Array(12).fill(0),
        disposal_sum: Array(12).fill(0),
        sales_amount: Array(12).fill(0),
        share_profit: Array(12).fill(0),
        customer_traffic: Array(12).fill(0), // Add for traffic
        deal_count: Array(12).fill(0), // Add for deal count
      },
      months: Array.from({ length: 12 }, (_, i) => `${i + 1}月`),
    };
  },
  async mounted() {
    await this.fetchSalesList();
    await this.fetchCustomerTraffic();
    this.fetchStats();
    this.fetchMonthlySummary();
    this.fetchCustomerSourceStats();
    this.fetchWeekendSalesStats(); // Call new fetch method
    this.fetchBedSalesStats();
  },
  beforeUnmount() {
    // Clean up any pending timeouts
    if (this.loadingTimeout) {
      clearTimeout(this.loadingTimeout);
    }
  },
  methods: {
    getSalesColor(salesName) {
      if (!this.salesColors[salesName]) {
        this.salesColors[salesName] = this.colorPalette[this.nextColorIndex % this.colorPalette.length];
        this.nextColorIndex++;
      }
      return this.salesColors[salesName];
    },
    async fetchSalesList() {
      try {
        const res = await fetch('/api/sales');
        if (!res.ok) throw new Error('Failed to fetch sales list');
        this.salesList = await res.json();
      } catch (e) {
        this.salesList = [];
      }
    },
    async fetchCustomerTraffic() {
      try{
        const trafficRes = await fetch(`/api/stat/customer_traffic?year=${this.selectedYear}`);
        const trafficJson = await trafficRes.json();
        
        // Update sales chart.
        const traffic = trafficJson.customer_traffic.map(x => x.value);
        const deal = trafficJson.deal_count.map(x => x.value);

        // Cache the traffic data and the year it was fetched for
        this.cachedTrafficData = {
          traffic: traffic,
          deal: deal
        }; 

        if (this.chartData) {
          this.chartData.datasets[2].data = traffic;
          this.chartData.datasets[3].data = deal;
        }
        
        // Update traffic chart.
        if (this.statTable) {
          this.statTable.customer_traffic = traffic;
          this.statTable.deal_count = deal;
        }
      } catch (e) { 
        console.log("Traffic", e);
        alert('載入客流量失敗');
      }
    },
    async fetchStats() {
      try {
        // Fetch sales_amount and total_profit per month
        const salesRes = await fetch(`/api/stat/sales_amount?year=${this.selectedYear}`);
        const salesJson = await salesRes.json();

        // Each API returns {sales_amount: [...], total_profit: [...]}, {customer_traffic: [...], deal_count: [...]}
        const months = Array.from({ length: 12 }, (_, i) => `${i + 1}月`);
        const sales = salesJson.sales_amount.map(x => x.value);
        const profit = salesJson.total_profit.map(x => x.value);

        // Take from cache or update after api returns.
        const traffic = this.cachedTrafficData ? this.cachedTrafficData.traffic : Array(12).fill(0);
        const deal = this.cachedTrafficData ? this.cachedTrafficData.deal : Array(12).fill(0);

        this.chartData = {
          labels: months,
          datasets: [
            {
              label: '營業額',
              data: sales,
              backgroundColor: 'rgba(54, 162, 235, 1)',
              yAxisID: 'y',
              type: 'bar',
            },
            {
              label: '商品毛利',
              data: profit,
              backgroundColor: 'rgba(255, 99, 132, 1)',
              yAxisID: 'y',
              type: 'bar',
            },
            {
              label: '來客數',
              data: traffic,
              backgroundColor: 'rgba(255, 206, 86, 1)',
              borderColor: 'rgba(255, 206, 86, 1)',
              yAxisID: 'y1',
              type: 'line',
              fill: false,
              tension: 0.3
            },
            {
              label: '成交數',
              data: deal,
              backgroundColor: 'rgba(56, 181, 56, 1)',
              borderColor: 'rgba(56, 181, 56, 1)',
              yAxisID: 'y1',
              type: 'line',
              fill: false,
              tension: 0.3
            }
          ]
        };
        // Dynamically set suggestedMax for y axis (15% buffer above max)
        const maxY = Math.max(...sales, ...profit);
        this.chartOptions.scales.y.suggestedMax = Math.ceil(maxY * 1.1);
      } catch (e) {
        console.log("Stat", e);
        alert('載入統計資料失敗');
      } finally {
        this.loading = false;
      }
    },
    async fetchMonthlySummary() {
      // Clear any existing timeout
      if (this.loadingTimeout) {
        clearTimeout(this.loadingTimeout);
      }
      
      // Set a delayed loading state to prevent flashing on fast requests
      this.loadingTimeout = setTimeout(() => {
        this.tableLoading = true;
      }, 200);
      
      try {
        let url = `/api/stat/monthly_summary?year=${this.selectedYear}`;
        if (this.selectedSalesId) {
          url += `&sales_id=${this.selectedSalesId}`;
        }
        const res = await fetch(url);
        if (!res.ok) throw new Error('Failed to fetch monthly summary');
        const data = await res.json();

        // Take from cache or update when api returns.
        const traffic = this.cachedTrafficData ? this.cachedTrafficData.traffic : Array(12).fill(0);
        const deal = this.cachedTrafficData ? this.cachedTrafficData.deal : Array(12).fill(0);
        this.statTable = {
          ...data, // Merge existing data from monthly_summary
          customer_traffic: traffic,
          deal_count: deal,
        };
      } catch (e) {
        this.statTable = {
          order_count: Array(12).fill(0),
          product_type_count: {},
          product_type_names: [],
          discount_sum: Array(12).fill(0),
          disposal_sum: Array(12).fill(0),
          sales_amount: Array(12).fill(0),
          share_profit: Array(12).fill(0),
          customer_traffic: Array(12).fill(0), // Initialize on error
          deal_count: Array(12).fill(0), // Initialize on error
        };
      } finally {
        // Clear the timeout and reset loading state
        if (this.loadingTimeout) {
          clearTimeout(this.loadingTimeout);
          this.loadingTimeout = null;
        }
        this.tableLoading = false;
      }
    },
    async fetchCustomerSourceStats() {
      try {
        const res = await fetch(`/api/stat/customer_source?year=${this.selectedYear}`);
        if (!res.ok) throw new Error('Failed to fetch customer source stats');
        const data = await res.json();

        const labels = data.map(item => item.source);
        const values = data.map(item => item.count);
        const backgroundColors = [
          '#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF', '#FF9F40',
          '#38b539', '#8D6E63', '#FFD700', '#ADFF2F', '#FF69B4', '#BA55D3'
        ];

        this.customerSourceChartData = {
          labels: labels,
          datasets: [
            {
              backgroundColor: backgroundColors.slice(0, labels.length),
              data: values
            }
          ]
        };
      } catch (e) {
        alert('載入客戶來源統計失敗');
        this.customerSourceChartData = null;
      }
    },
    async fetchWeekendSalesStats() {
      try {
        let url = `/api/stat/weekend_sales?year=${this.selectedYear}`;
        if (this.selectedMonth !== 0) {
          url += `&month=${this.selectedMonth}`;
        }
        const res = await fetch(url);
        if (!res.ok) throw new Error('Failed to fetch weekend sales stats');
        const data = await res.json();

        // Unique labels (Dates)
        const dateLabelsMap = new Map();
        data.forEach(item => {
          const dateStr = `${new Date(item.date).getMonth() + 1}/${new Date(item.date).getDate()} ${item.day_of_week}`;
          if (!dateLabelsMap.has(item.date)) {
            dateLabelsMap.set(item.date, {
              dateStr,
              customer_count: item.customer_count,
              deal_count: item.deal_count
            });
          }
        });

        const sortedDates = Array.from(dateLabelsMap.keys()).sort();
        const labels = sortedDates.map(d => dateLabelsMap.get(d).dateStr);

        // Group by Salesperson
        const salesDataMap = new Map();
        data.forEach(item => {
          if (!salesDataMap.has(item.sales_name)) {
            salesDataMap.set(item.sales_name, new Array(sortedDates.length).fill(0));
          }
          const dateIndex = sortedDates.indexOf(item.date);
          salesDataMap.get(item.sales_name)[dateIndex] = item.sales_amount;
        });

        const datasets = [];

        // Add bar datasets for each salesperson (grouped)
        salesDataMap.forEach((salesAmounts, salesName) => {
          datasets.push({
            label: salesName,
            data: salesAmounts,
            backgroundColor: this.getSalesColor(salesName),
            yAxisID: 'y',
            type: 'bar',
            order: 2
          });
        });

        // Add line datasets last (drawn on top)
        datasets.push({
          label: '來客數',
          data: sortedDates.map(d => dateLabelsMap.get(d).customer_count),
          backgroundColor: 'rgba(255, 206, 86, 1)',
          borderColor: 'rgba(255, 206, 86, 1)',
          yAxisID: 'y1',
          type: 'line',
          fill: false,
          tension: 0.3,
          order: 1
        });
        datasets.push({
          label: '成交數',
          data: sortedDates.map(d => dateLabelsMap.get(d).deal_count),
          backgroundColor: 'rgba(56, 181, 56, 1)',
          borderColor: 'rgba(56, 181, 56, 1)',
          yAxisID: 'y1',
          type: 'line',
          fill: false,
          tension: 0.3,
          order: 1
        });

        this.weekendSalesChartData = {
          labels: labels,
          datasets: datasets
        };

        // Dynamically set suggestedMax for axes
        const allSalesValues = datasets.filter(ds => ds.type === 'bar').flatMap(ds => ds.data);
        const allTrafficValues = datasets.filter(ds => ds.type === 'line').flatMap(ds => ds.data);
        
        const maxY = allSalesValues.length > 0 ? Math.max(...allSalesValues) : 0;
        const maxY1 = allTrafficValues.length > 0 ? Math.max(...allTrafficValues) : 0;

        this.weekendSalesChartOptions.scales.y.suggestedMax = Math.ceil(maxY * 1.1);
        this.weekendSalesChartOptions.scales.y1.suggestedMax = Math.ceil(maxY1 * 1.1);
      } catch (e) {
        console.log("Weekend Sales Stat", e);
        alert('載入週末銷售統計失敗');
        this.weekendSalesChartData = null;
      }
    },
    handleSalesChange() {
      this.fetchMonthlySummary();
    },
    async handleYearChange() {
      this.loading = true;
      // Update default month based on year selection
      if (this.selectedYear < this.currentYear) {
        this.selectedMonth = 12; // December for past years
      } else {
        this.selectedMonth = 0; // "最近一個月" for current year
      }
      this.fetchCustomerSourceStats();
      await this.fetchCustomerTraffic();
      this.fetchStats();
      this.fetchMonthlySummary();
      this.fetchWeekendSalesStats(); // Call new fetch method on year change
      this.fetchBedSalesStats();
    },
    async fetchBedSalesStats() {
      try {
        let url = `/api/stat/bed_sales?year=${this.selectedYear}`;
        if (this.selectedBedMonth !== 0) {
          url += `&month=${this.selectedBedMonth}`;
        }
        const res = await fetch(url);
        if (!res.ok) throw new Error('Failed to fetch bed sales stats');
        const data = await res.json();
        this.bedSalesData = data;
        this.renderBedCharts();
      } catch (e) {
        console.error("Bed Sales Stat", e);
        this.bedSalesData = null;
      }
    },
    renderBedCharts() {
      if (!this.bedSalesData) return;

      const top20 = this.bedSalesData.slice(0, 20);
      const totalAll = this.bedSalesData.reduce((sum, item) => sum + item.count, 0);

      // Bar Chart Data (Top 20 with total stored for percentage calculation)
      this.bedBarChartData = {
        labels: top20.map(item => item.name),
        datasets: [{
          label: '銷售數量',
          data: top20.map(item => item.count),
          backgroundColor: '#36A2EB',
          total: totalAll
        }]
      };
    },
    formatCurrency(val) {
      const v = val || 0;
      return new Intl.NumberFormat('zh-TW', {
        style: 'currency',
        currency: 'TWD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(v);
    },
  },
};

</script>

<style scoped>
.stat-chart-container {
  max-width: fit-content;
  min-width: 1200px;
  margin: 40px auto;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
  padding: 32px 24px;
}
.chart-container {
  display: flex;
  justify-content: center;
  align-items: center;
}
.chart-inner {
  width: 1000px;
  min-height: 800px;
  margin: 0 auto;
}
.sales-selector-row {
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
}
.year-selector-row, .month-selector-row {
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
}
.month-selector-row {
  justify-content: center;
}
.tab-navigation {
  margin-bottom: 20px;
  display: flex;
  justify-content: center; /* Center the tabs */
  gap: 0; /* Remove gap for segmented look */
  border: 1px solid #2c3e50; /* iOS-like border */
  border-radius: 10px; /* Rounded corners for the whole tab group */
  overflow: hidden; /* Ensures inner buttons respect border-radius */
  width: fit-content; /* Shrink to fit content */
  margin-left: auto; /* Center horizontally */
  margin-right: auto; /* Center horizontally */
}

.tab-navigation button {
  padding: 10px 20px;
  border: none; /* Remove individual button borders */
  background-color: transparent; /* Default transparent background */
  cursor: pointer;
  font-size: 16px;
  color: #2c3e50; /* Default text color */
  transition: background-color 0.3s ease, color 0.3s ease; /* Smooth transitions */
}

.tab-navigation button:first-child {
  border-top-left-radius: 9px; /* Match parent border-radius */
  border-bottom-left-radius: 9px;
}

.tab-navigation button:last-child {
  border-top-right-radius: 9px; /* Match parent border-radius */
  border-bottom-right-radius: 9px;
}

.tab-navigation button.active {
  background-color: #2c3e50; /* Active background color */
  color: white; /* Active text color */
}

.tab-navigation button:not(:last-child) {
  border-right: 1px solid #2c3e50; /* Separator between buttons */
}
.stat-table-wrapper {
  margin-top: 32px;
  overflow-x: auto;
  position: relative;
  transition: opacity 0.3s ease;
}

.stat-table-wrapper.loading-state {
  opacity: 0.6;
}

.stat-table-wrapper.loading-state::before {
  content: '載入中...';
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: rgba(255, 255, 255, 0.9);
  padding: 8px 16px;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  font-size: 1.1em;
  color: #666;
  z-index: 10;
  pointer-events: none;
}
.stat-table {
  width: 100%;
  border-collapse: collapse;
  background: #fafbfc;
}
.stat-table th, .stat-table td {
  border: 1px solid #e0e0e0;
  padding: 8px 12px;
  text-align: right;
}
.stat-table th {
  background: #f0f4f8;
  text-align: center;
}
.stat-table td:first-child, .stat-table th:first-child {
  text-align: left;
}
.loading {
  text-align: center;
  font-size: 1.2em;
  color: #888;
  margin: 40px 0;
}
</style>
